import React, { useState } from "react";
import PageTopBar from "../../../../components/common/PageTopBar";
import { toolbarButtonSx } from "../../../../components/common/toolbarStyles";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useGrades } from "../../hooks/useGrades";
import { useSections } from "../../hooks/useSections";
import { Grade, Section } from "../../types";
import { useNotify } from "../../../../context/NotificationContext";

const suggestCode = (name: string) => name.trim().replace(/\s+/g, "_");

type AddForm = { name: string; code: string; promoteTo: string; numericValue: string };
const emptyAddForm: AddForm = { name: "", code: "", promoteTo: "", numericValue: "" };

/** Checkbox list of every section, ticked by code. */
const SectionChecklist: React.FC<{
  sections: Section[];
  loading: boolean;
  checked: Set<string>;
  onToggle: (code: string) => void;
}> = ({ sections, loading, checked, onToggle }) =>
  loading ? (
    <Box display="flex" justifyContent="center" py={2}>
      <CircularProgress size={24} />
    </Box>
  ) : sections.length === 0 ? (
    <Typography variant="body2" color="text.secondary">
      No sections yet. Add sections first.
    </Typography>
  ) : (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5, maxHeight: 240, overflowY: "auto" }}>
      <FormGroup>
        {sections.map((section) => (
          <FormControlLabel
            key={section.code}
            control={<Checkbox checked={checked.has(section.code)} onChange={() => onToggle(section.code)} />}
            label={section.name}
          />
        ))}
      </FormGroup>
    </Paper>
  );

/** `tabs`: when shown inside Academics → Setup, the tabs replace the title in the top bar. */
const GradesUI: React.FC<{ tabs?: React.ReactNode }> = ({ tabs }) => {
  const notify = useNotify();
  const { grades, loading, saving, error, isDuplicate, actions } = useGrades();
  const { sections: allSections, loading: sectionsLoading } = useSections();

  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; code: string } | null>(null);
  const [editingGrade, setEditingGrade] = useState<Grade | null>(null);
  const [checkedSections, setCheckedSections] = useState<Set<string>>(new Set());

  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState<AddForm>(emptyAddForm);
  const [codeTouched, setCodeTouched] = useState(false);
  const [addErrors, setAddErrors] = useState<Partial<Record<keyof AddForm, string>>>({});

  const toggle = (code: string) => {
    setCheckedSections((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  // ---------- edit sections ----------
  const openEdit = (grade: Grade) => {
    setEditingGrade(grade);
    setCheckedSections(new Set(grade.sections.map((section) => section.code)));
    setMenuAnchor(null);
  };

  const closeEdit = () => {
    setEditingGrade(null);
    setCheckedSections(new Set());
  };

  const handleSaveSections = async () => {
    if (!editingGrade) return;
    const failure = await actions.saveSections(editingGrade.code, Array.from(checkedSections));
    if (failure) {
      notify(failure, "error");
    } else {
      notify("Sections updated successfully.", "success");
      closeEdit();
    }
  };

  const handleRemoveSection = async (grade: Grade, sectionCode: string) => {
    const failure = await actions.removeSection(grade.code, sectionCode);
    notify(failure ?? "Section removed.", failure ? "error" : "success");
  };

  // ---------- add grade ----------
  const openAdd = () => {
    setAddForm(emptyAddForm);
    setCodeTouched(false);
    setAddErrors({});
    setCheckedSections(new Set());
    setAddOpen(true);
  };

  const closeAdd = () => {
    setAddOpen(false);
    setCheckedSections(new Set());
  };

  const setAddField = (field: keyof AddForm, value: string) => {
    setAddForm((prev) => {
      const next = { ...prev, [field]: value };
      // suggest the code from the name until the user edits the code
      if (field === "name" && !codeTouched) next.code = suggestCode(value);
      return next;
    });
    if (field === "code") setCodeTouched(true);
    setAddErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleCreate = async () => {
    const errors: Partial<Record<keyof AddForm, string>> = {};
    const name = addForm.name.trim();
    const code = addForm.code.trim();
    const promoteTo = addForm.promoteTo.trim();
    if (!name) errors.name = "Name is required.";
    else if (isDuplicate("name", name)) errors.name = "A grade with this name already exists.";
    if (!code) errors.code = "Code is required.";
    else if (isDuplicate("code", code)) errors.code = "A grade with this code already exists.";
    if (!promoteTo) errors.promoteTo = "Promote to is required.";
    if (addForm.numericValue && Number.isNaN(Number(addForm.numericValue)))
      errors.numericValue = "Must be a number.";
    setAddErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const failure = await actions.create({
      code,
      name,
      promoteTo,
      numericValue: addForm.numericValue ? Number(addForm.numericValue) : undefined,
      sectionCodes: Array.from(checkedSections),
    });
    if (failure) {
      notify(failure, "error");
    } else {
      notify("Grade created successfully.", "success");
      closeAdd();
    }
  };

  return (
    <Box>
      <PageTopBar
        left={tabs ?? "Grades"}
        right={
          <Button variant="contained" size="small" sx={toolbarButtonSx} startIcon={<AddIcon />} onClick={openAdd}>
            Add Grade
          </Button>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "grey.50" }}>
              <TableCell sx={{ fontWeight: 600, py: 1 }}>#</TableCell>
              <TableCell sx={{ fontWeight: 600, py: 1 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600, py: 1 }}>Code</TableCell>
              <TableCell sx={{ fontWeight: 600, py: 1 }}>Sections</TableCell>
              <TableCell sx={{ fontWeight: 600, py: 1, pr: 4 }} align="right">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : grades.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    No grades found. Create one below.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              grades.map((grade, index) => (
                <TableRow key={grade.code} hover sx={{ "&:last-child td": { border: 0 } }}>
                  <TableCell sx={{ py: 0.5 }}>{index + 1}</TableCell>
                  <TableCell sx={{ py: 0.5 }}>{grade.name}</TableCell>
                  <TableCell sx={{ py: 0.5 }}>{grade.code}</TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    {grade.sections.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    ) : (
                      <Box display="flex" gap={0.5} flexWrap="wrap">
                        {grade.sections.map((section) => (
                          <Chip
                            key={section.code}
                            label={section.name}
                            size="small"
                            onDelete={saving ? undefined : () => handleRemoveSection(grade, section.code)}
                          />
                        ))}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ py: 0.5, pr: 4 }}>
                    <IconButton size="small" onClick={(event) => setMenuAnchor({ el: event.currentTarget, code: grade.code })}>
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu anchorEl={menuAnchor?.el} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem
          onClick={() => {
            const grade = grades.find((g) => g.code === menuAnchor?.code);
            if (grade) openEdit(grade);
          }}
        >
          Edit sections
        </MenuItem>
      </Menu>

      {/* Edit sections */}
      <Dialog open={Boolean(editingGrade)} onClose={closeEdit} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 600, pb: 1.5 }}>Edit Sections</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Paper variant="outlined" sx={{ p: 1.5, mb: 3, borderRadius: 1.5, bgcolor: "grey.50" }}>
            <Typography variant="caption" color="text.secondary">
              Grade
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {editingGrade?.name}
            </Typography>
          </Paper>
          <Typography variant="subtitle2" fontWeight={600} mb={1}>
            Sections
          </Typography>
          <SectionChecklist sections={allSections} loading={sectionsLoading} checked={checkedSections} onToggle={toggle} />
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeEdit} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveSections} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add grade */}
      <Dialog open={addOpen} onClose={closeAdd} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 600, pb: 1.5 }}>New Grade</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <TextField
            label="Name"
            autoFocus
            fullWidth
            size="small"
            value={addForm.name}
            onChange={(e) => setAddField("name", e.target.value)}
            error={Boolean(addErrors.name)}
            helperText={addErrors.name}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Code"
            fullWidth
            size="small"
            value={addForm.code}
            onChange={(e) => setAddField("code", e.target.value)}
            error={Boolean(addErrors.code)}
            helperText={addErrors.code ?? "Can't be changed after the grade is created."}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Promote to"
            fullWidth
            size="small"
            value={addForm.promoteTo}
            onChange={(e) => setAddField("promoteTo", e.target.value)}
            error={Boolean(addErrors.promoteTo)}
            helperText={addErrors.promoteTo ?? "Name of the grade students move to on promotion."}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Numeric value (optional)"
            fullWidth
            size="small"
            value={addForm.numericValue}
            onChange={(e) => setAddField("numericValue", e.target.value)}
            error={Boolean(addErrors.numericValue)}
            helperText={addErrors.numericValue}
            sx={{ mb: 3 }}
          />
          <Typography variant="subtitle2" fontWeight={600} mb={1}>
            Sections (optional)
          </Typography>
          <SectionChecklist sections={allSections} loading={sectionsLoading} checked={checkedSections} onToggle={toggle} />
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeAdd} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreate} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GradesUI;
